import clsx from 'clsx';
import React from 'react';
import { getSetNewPasswordSchema } from './schema';
import { useFormik } from 'formik';
import Input from '../../components/Input';
import Button from '../../components/Button';
import { useButtonSizeLarge } from '../../utils/buttonSizes';
import { useLocation } from 'react-router';
import { toast } from 'react-toastify';
import { useApiClients } from '../../client/api/useApiClient';

interface SetNewPasswordProps {
  otherClasses?: string;
  changeSuccessfuly: () => void;
}

const INITIAL_STATE = {
  password: '',
  confirmPassword: '',
};

const SetNewPassword: React.FC<SetNewPasswordProps> = ({
  otherClasses,
  changeSuccessfuly,
}) => {
  const setNewPasswordClasses = clsx(
    otherClasses,
    'w-full max-w-325 lg:max-w-465 mx-auto flex flex-col items-center gap-5 md:gap-8 lg:gap-10',
  );

  const location = useLocation();
  const query = new URLSearchParams(location?.search);
  const userId = query.get('userId');
  const token = query.get('token');

  const { AuthApi } = useApiClients();

  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    handleBlur,
    touched,
    isSubmitting,
    resetForm,
  } = useFormik({
    initialValues: INITIAL_STATE,
    validationSchema: getSetNewPasswordSchema(),
    onSubmit: async (values) => {
      try {
        const response = await AuthApi.authControllerSetPassword({
          setPasswordDto: {
            password: values?.password,
            token: String(token),
            userId: String(userId),
          },
        });
        toast.success(response?.data?.message);
        resetForm();
        changeSuccessfuly();
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });
  const sizes = useButtonSizeLarge();

  return (
    <form
      action="#"
      onSubmit={handleSubmit}
      className={setNewPasswordClasses}
      data-testid="set-new-password"
      aria-labelledby="set-new-password-title"
      noValidate
    >
      <Input
        onBlur={handleBlur}
        label="New Password*"
        errorMessage={
          touched.password && errors.password ? errors.password : ''
        }
        disabled={isSubmitting}
        value={values.password}
        type="password"
        placeholder="New Password"
        onChange={handleChange}
        name="password"
        otherClasses={{ container: 'w-full' }}
        aria-required={true}
        aria-invalid={touched.password && !!errors.password}
        aria-describedby={
          touched.password && errors.password ? 'password-error' : undefined
        }
      />
      <Input
        onBlur={handleBlur}
        label="Retype New Password*"
        errorMessage={
          touched.confirmPassword && errors.confirmPassword
            ? errors.confirmPassword
            : ''
        }
        disabled={isSubmitting}
        value={values.confirmPassword}
        type="password"
        placeholder="Retype Password"
        onChange={handleChange}
        name="confirmPassword"
        otherClasses={{ container: 'w-full' }}
        aria-required={true}
        aria-invalid={touched.confirmPassword && !!errors.confirmPassword}
        aria-describedby={
          touched.confirmPassword && errors.confirmPassword
            ? 'confirm-password-error'
            : undefined
        }
      />
      <Button
        size={sizes}
        otherClasses="w-full max-w-465 sm:mt-0 mt-2"
        type="submit"
        aria-label="Set New Password"
        disable={isSubmitting}
      >
        Set New Password
      </Button>
    </form>
  );
};

export default SetNewPassword;
